// src/pages/WhatsAppCampaignSummary.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  FiBarChart2,
  FiCalendar,
  FiRefreshCw,
  FiArrowLeft,
  FiAlertCircle,
  FiHash,
  FiSearch,
} from "react-icons/fi";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

/* -------------------------
   Helpers / UI bits
-------------------------- */
const STATUS_META = {
  ACTIVE: { label: "active", bg: "bg-green-100", text: "text-green-800", ring: "ring-green-200" },
  SCHEDULED: { label: "scheduled", bg: "bg-brand-100", text: "text-brand-800", ring: "ring-brand-200" },
  ENDED: { label: "ended", bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-200" },
  DELETED: { label: "deleted", bg: "bg-red-100", text: "text-red-800", ring: "ring-red-200" },
  UNKNOWN: { label: "unknown", bg: "bg-gray-100", text: "text-gray-700", ring: "ring-gray-200" },
};

function Badge({ status }) {
  const meta = STATUS_META[status] || STATUS_META.UNKNOWN;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.text} ring-1 ${meta.ring}`}>
      {meta.label}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
}

function SkeletonBlock() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm animate-pulse">
      <div className="h-5 w-48 bg-gray-200 rounded mb-4" />
      <div className="h-72 w-full bg-gray-200 rounded" />
    </div>
  );
}

const fmtInt = (n) => (n === null || n === undefined ? "—" : Number(n).toLocaleString("en-IN"));
const fmtMoneyINR = (n) =>
  n === null || n === undefined
    ? "—"
    : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n));
const fmtDT = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return String(s);
    return d.toLocaleString(); // local human-readable
  } catch {
    return String(s);
  }
};

/* -------------------------
   Page
-------------------------- */
export default function WhatsAppCampaignSummary() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Accept campaign from the list page OR via ?campaign_id=
  const stateCampaign = location.state?.campaign || null;
  const qpCampaignId = searchParams.get("campaign_id");
  const campaignId = stateCampaign?.campaign_id || qpCampaignId || "";

  // API URLs — relative, so they follow whichever stack served this page.
  // These named https://maa.ycpay.in outright, which meant the parallel stack
  // reported on LIVE campaigns and, being cross-origin, sent no session cookie.
  const statsUrl = useMemo(() => (campaignId ? `/api/whatsapp-campaigns/${campaignId}/stats` : ""), [campaignId]);
  const messagesUrl = useMemo(() => (campaignId ? `/api/campaigns/${campaignId}/messages?limit=500&offset=0` : ""), [campaignId]);

  // State
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [stats, setStats] = useState(null); // raw stats response

  // Message log state
  const [loadingLog, setLoadingLog] = useState(false);
  const [logErr, setLogErr] = useState("");
  const [logRows, setLogRows] = useState([]); // array of { phonenumber, message_id, template_name, sent_time, delivered_time, read_time, retries, status? }
  const [logQuery, setLogQuery] = useState("");

  const fetchStats = useCallback(async () => {
    if (!statsUrl) return;
    setLoading(true);
    setErr("");
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 20000);
    try {
      const res = await fetch(statsUrl, { credentials: "include", headers: { Accept: "application/json" }, signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${res.statusText}`);
      const data = await res.json();
      setStats(data);
    } catch (e) {
      setErr(e?.name === "AbortError" ? "Request timed out. Please retry." : e?.message || "Failed to fetch stats.");
      setStats(null);
    } finally {
      clearTimeout(t);
      setLoading(false);
    }
  }, [statsUrl]);

  // Fetch message log (fallbacks if stats already provides it)
  const fetchMessageLog = useCallback(async () => {
    // If stats already contained a detail array, reuse it
    const embedded =
      stats?.items || stats?.messages || stats?.details || stats?.rows || stats?.log || stats?.campaign_items;
    if (Array.isArray(embedded) && embedded.length) {
      setLogRows(embedded);
      setLogErr("");
      return;
    }
    if (!messagesUrl) {
      setLogRows([]);
      setLogErr("");
      return;
    }
    setLoadingLog(true);
    setLogErr("");
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 20000);
    try {
      const res = await fetch(messagesUrl, { credentials: "include", headers: { Accept: "application/json" }, signal: ctrl.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status} — ${res.statusText}`);
      const data = await res.json();
      const arr = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setLogRows(arr);
    } catch (e) {
      setLogRows([]);
      setLogErr(e?.name === "AbortError" ? "Request timed out. Please retry." : e?.message || "Failed to fetch message log.");
    } finally {
      clearTimeout(t);
      setLoadingLog(false);
    }
  }, [messagesUrl, stats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchMessageLog();
  }, [fetchMessageLog]);

  // Normalize meta
  const meta = useMemo(() => {
    // 1. Adapter for flat CampaignStatsResponse from API
    if (stats?.campaign_id && stats?.date_range) {
      return {
        campaign_name: stats.campaign_name,
        campaign_id: stats.campaign_id,
        start_date: stats.date_range.from,
        end_date: stats.date_range.to,
        campaign_budget: stats.budget,
        unit_cost: stats.unit_cost,
        max_messages: stats.max_messages,
        status: stats.lifecycle_status || stats.header_status || "UNKNOWN",
        template_category: stats.template_category,
      };
    }
    // 2. Fallback / legacy structure
    return (
      stats?.campaign || stats?.meta || {
        campaign_name: stateCampaign?.campaign_name || "WhatsApp Campaign",
        campaign_id: campaignId,
        start_date: stateCampaign?.start_date || "",
        end_date: stateCampaign?.end_date || "",
        campaign_budget: stateCampaign?.campaign_budget || 0,
        unit_cost: stateCampaign?.unit_cost || 0.786,
        max_messages: stateCampaign?.max_messages || 0,
        status: stateCampaign?.status || "UNKNOWN",
        template_category: stateCampaign?.template_category || "",
      }
    );
  }, [stats, stateCampaign, campaignId]);

  const counts = useMemo(() => {
    const c = stats?.counts || {};
    return {
      attempted: Number(c.attempted || 0),
      sent: Number(c.sent || 0),
      delivered: Number(c.delivered || 0),
      read: Number(c.read || 0),
      failed: Number(c.failed || 0),
      skipped: Number(c.skipped || 0),
    };
  }, [stats]);

  // Derived KPIs
  const kpis = useMemo(() => {
    const unit = Number(meta?.unit_cost || 0.786);
    const budget = Number(meta?.campaign_budget || 0);
    const sent = counts.sent;
    const delivered = counts.delivered;
    const read = counts.read;

    const costSpent = sent * unit;
    const utilization = budget > 0 ? (costSpent / budget) * 100 : 0;
    const remaining = Math.max(0, budget - costSpent);
    const deliveryRate = sent > 0 ? (delivered / sent) * 100 : 0;
    const readRate = delivered > 0 ? (read / delivered) * 100 : 0;
    const sendRate = meta?.max_messages ? (sent / Number(meta.max_messages)) * 100 : 0;

    return {
      costSpent,
      utilization,
      remaining,
      deliveryRate,
      readRate,
      sendRate,
      ...counts,
    };
  }, [counts, meta]);

  // Daily rows (if provided)
  const daily = useMemo(() => {
    const rows = stats?.daily || stats?.rows_daily || [];
    return rows
      .map((r) => ({
        date: r.date,
        attempted: Number(r.attempted || 0),
        sent: Number(r.sent || 0),
        delivered: Number(r.delivered || 0),
        read: Number(r.read || 0),
      }))
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [stats]);

  // Chart data with derived cost + cumulative utilization
  const chartDaily = useMemo(() => {
    const unit = Number(meta?.unit_cost || 0.786);
    let cumCost = 0;
    return daily.map((d) => {
      const dayCost = d.sent * unit;
      cumCost += dayCost;
      const util = Number(meta?.campaign_budget || 0) > 0 ? (cumCost / Number(meta.campaign_budget)) * 100 : 0;
      return { ...d, cost: dayCost, cumUtil: util };
    });
  }, [daily, meta]);

  // Status badge (derive if missing)
  const status = useMemo(() => {
    // If backend provides a status, trust it
    const s = (meta?.status || "").toUpperCase();
    if (s && STATUS_META[s]) return s;

    // Fallback heuristic
    const today = new Date().toISOString().slice(0, 10);
    if (meta?.end_date && meta.end_date < today) return "ENDED";
    if (meta?.start_date && meta.start_date <= today && (!meta?.end_date || meta.end_date >= today)) return "ACTIVE";
    return "SCHEDULED";
  }, [meta]);

  const title = meta?.campaign_name || `Campaign ${campaignId}`;

  // derive status per row (read > delivered > sent; else fallback to row.status)
  const deriveRowStatus = (row) => {
    if (row?.status) return String(row.status).toLowerCase();
    if (row?.read_time) return "read";
    if (row?.delivered_time) return "delivered";
    if (row?.sent_time) return "sent";
    return "queued";
  };

  // Filtered message log (search by phone or message id or template)
  const filteredLog = useMemo(() => {
    const q = logQuery.trim().toLowerCase();
    if (!q) return logRows;
    return logRows.filter((r) => {
      return (
        String(r.phonenumber || r.phone_number || "")
          .toLowerCase()
          .includes(q) ||
        String(r.message_id || "")
          .toLowerCase()
          .includes(q) ||
        String(r.template_name || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [logRows, logQuery]);

  // Manual Trigger for Scheduled
  const handleProcessScheduled = async () => {
    if (!confirm("Are you sure you want to trigger this scheduled campaign now?")) return;
    setLoading(true);
    try {
      // This one sends real messages to real customers, so it mattered most
      // that it was pointed at the live host from every stack.
      const res = await fetch(`/api/campaigns/${campaignId}/process-scheduled`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || res.statusText);
      }
      alert("Campaign Triggered!");
      fetchStats();
    } catch (e) {
      alert("Error: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // Custom tooltip formatting
  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div className="rounded-lg border border-gray-200 bg-white/95 px-3 py-2 shadow-md">
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        {payload.map((p) => (
          <div key={p.dataKey} className="text-sm">
            <span className="font-medium">{p.name}: </span>
            <span>
              {p.name?.toLowerCase().includes("cost") || p.name?.toLowerCase().includes("util")
                ? p.name?.toLowerCase().includes("util")
                  ? `${Number(p.value).toFixed(1)}%`
                  : fmtMoneyINR(p.value)
                : fmtInt(p.value)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8">
        <div className="mx-auto" style={{ maxWidth: "1800px" }}>
          {/* Header */}
          <div className="mb-6 flex flex-col items-stretch justify-between gap-3 sm:flex-row sm:items-start sm:gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100" title="Back">
                  <FiArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">WhatsApp — Campaign Summary</h1>
              </div>
              <p className="text-sm sm:text-base text-gray-600">Campaign details and performance overview</p>
            </div>
            <div className="flex items-center gap-2">
              {status === "SCHEDULED" && (
                <button
                  onClick={handleProcessScheduled}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-ink rounded-lg hover:bg-brand-600 transition-colors"
                >
                  <FiBarChart2 className="w-4 h-4" /> Process Now
                </button>
              )}
              <button
                onClick={() => {
                  fetchStats();
                  fetchMessageLog();
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-brand-500 text-ink rounded-lg hover:bg-brand-600 transition-colors"
              >
                <FiRefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
          </div>

          {/* Campaign details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900 truncate">{title}</h2>
                  <Badge status={status} />
                  <span className="text-[11px] text-gray-400 font-mono inline-flex items-center gap-1" title="Campaign UUID">
                    <FiHash className="w-3.5 h-3.5" />
                    {String(meta?.campaign_id || campaignId).slice(0, 8)}
                  </span>
                  {meta?.template_category && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-800 ring-1 ring-brand-200">
                      {meta.template_category}
                    </span>
                  )}
                </div>

                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Budget</div>
                    <div className="font-medium text-gray-900">{fmtMoneyINR(meta?.campaign_budget)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Unit Cost</div>
                    <div className="font-medium text-gray-900">{fmtMoneyINR(meta?.unit_cost || 0.786)}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Max Messages</div>
                    <div className="font-medium text-gray-900">{fmtInt(meta?.max_messages)}</div>
                  </div>
                  <div className="flex items-center gap-2 col-span-1 sm:col-span-2">
                    <FiCalendar className="w-4 h-4 text-gray-500" />
                    <div className="text-sm text-gray-700 flex flex-wrap gap-2">
                      <span className="font-medium">{fmtDT(meta?.start_date)}</span>
                      <span className="text-gray-400">–</span>
                      <span className="font-medium">{fmtDT(meta?.end_date)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* API load error */}
            {loading && <div className="mt-3 text-sm text-gray-500">Loading…</div>}
            {err && (
              <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-start gap-2">
                <FiAlertCircle className="w-4 h-4 mt-0.5" />
                <span>{err}</span>
              </div>
            )}
          </div>

          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <StatCard label="Attempted" value={fmtInt(kpis.attempted)} />
            <StatCard label="Sent" value={fmtInt(kpis.sent)} sub={`Send Rate ${kpis.sendRate.toFixed(1)}%`} />
            <StatCard label="Delivered" value={fmtInt(kpis.delivered)} sub={`Delivery Rate ${kpis.deliveryRate.toFixed(1)}%`} />
            <StatCard label="Read" value={fmtInt(kpis.read)} sub={`Read Rate ${kpis.readRate.toFixed(1)}%`} />
            <StatCard label="Failed" value={fmtInt(kpis.failed)} />
            <StatCard label="Skipped" value={fmtInt(kpis.skipped)} />
          </div>

          {/* Money/Utilization Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <StatCard label="Cost Spent" value={fmtMoneyINR(kpis.costSpent)} />
            <StatCard label="Budget Remaining" value={fmtMoneyINR(kpis.remaining)} />
            <StatCard label="Budget Utilization" value={`${kpis.utilization.toFixed(1)}%`} />
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Daily Sent / Delivered / Read */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Daily Sent / Delivered / Read</h3>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <FiBarChart2 /> Overall window
                </div>
              </div>
              {loading ? (
                <SkeletonBlock />
              ) : daily.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                  <FiBarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-gray-700">No daily breakdown available.</div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartDaily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      <Bar dataKey="sent" name="Sent" fill="#F96168" />
                      <Bar dataKey="delivered" name="Delivered" fill="#16a34a" />
                      <Bar dataKey="read" name="Read" fill="#f59e0b" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Daily Cost + Cumulative Utilization */}
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Daily Cost & Cumulative Utilization</h3>
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <FiBarChart2 /> Overall window
                </div>
              </div>
              {loading ? (
                <SkeletonBlock />
              ) : daily.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
                  <FiBarChart2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <div className="text-gray-700">No daily breakdown available.</div>
                </div>
              ) : (
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartDaily} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" />
                      <Tooltip content={<ChartTooltip />} />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="cost" name="Cost (INR)" dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="cumUtil" name="Cumulative Utilization (%)" dot />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>

          {/* Daily table */}
          <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Breakdown</h3>
            {loading ? (
              <SkeletonBlock />
            ) : daily.length === 0 ? (
              <div className="text-gray-600">No rows to display.</div>
            ) : (
              <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                <table className="min-w-[640px] w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-4">Date</th>
                      <th className="py-2 pr-4">Attempted</th>
                      <th className="py-2 pr-4">Sent</th>
                      <th className="py-2 pr-4">Delivered</th>
                      <th className="py-2 pr-4">Read</th>
                      <th className="py-2 pr-4">Cost (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartDaily.map((r) => (
                      <tr key={r.date} className="border-b last:border-0">
                        <td className="py-2 pr-4">{r.date}</td>
                        <td className="py-2 pr-4">{fmtInt(r.attempted)}</td>
                        <td className="py-2 pr-4">{fmtInt(r.sent)}</td>
                        <td className="py-2 pr-4">{fmtInt(r.delivered)}</td>
                        <td className="py-2 pr-4">{fmtInt(r.read)}</td>
                        <td className="py-2 pr-4">{fmtMoneyINR(r.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Message Log (phone, message_id, status) */}
          <div className="mt-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Message Log</h3>
              <div className="w-full md:w-80 relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  value={logQuery}
                  onChange={(e) => setLogQuery(e.target.value)}
                  placeholder="Filter by phone, message ID, or template…"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            {loadingLog ? (
              <SkeletonBlock />
            ) : logErr ? (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800 flex items-start gap-2">
                <FiAlertCircle className="w-4 h-4 mt-0.5" />
                <span>{logErr}</span>
              </div>
            ) : filteredLog.length === 0 ? (
              <div className="text-gray-600">No message rows to display.</div>
            ) : (
              <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
                <table className="min-w-[640px] w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-600 border-b">
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">Template</th>
                      <th className="py-2 pr-4">Message ID</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Sent</th>
                      <th className="py-2 pr-4">Delivered</th>
                      <th className="py-2 pr-4">Read</th>
                      <th className="py-2 pr-4">Retries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLog.map((r, idx) => {
                      const phone = r.phonenumber || r.phone_number || r.recipient_phone || "";
                      const statusRow = deriveRowStatus(r);
                      return (
                        <tr key={`${r.message_id || phone || idx}`} className="border-b last:border-0">
                          <td className="py-2 pr-4">{phone}</td>
                          <td className="py-2 pr-4">{r.template_name || "—"}</td>
                          <td className="py-2 pr-4 font-mono text-xs text-gray-700 break-all">{r.message_id || "—"}</td>
                          <td className="py-2 pr-4 capitalize">{statusRow}</td>
                          <td className="py-2 pr-4">{fmtDT(r.sent_time)}</td>
                          <td className="py-2 pr-4">{fmtDT(r.delivered_time)}</td>
                          <td className="py-2 pr-4">{fmtDT(r.read_time)}</td>
                          <td className="py-2 pr-4">{fmtInt(r.retries || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
